import { useState } from "react";
import { Plus, X } from "lucide-react";
import { AMENITY_PRESETS } from "../../../lib/seed";

/* Step 4 — Amenities. Stored internally as a string[] of amenity names.
   Tolerant of legacy/round-tripped shapes: an amenity may arrive as a
   plain string OR as an object { title, desc?, icon? } (the API shape).
   `asName` normalizes either to its display string so the UI never tries
   to render an object as a React child (which previously white-screened). */
const asName = (a) => (typeof a === "string" ? a : a?.title || a?.name || "");

export function AmenitiesStep({ data, setData }) {
  // Normalize whatever is in data.amenities to a string[] for the UI.
  const selected = (data.amenities || []).map(asName).filter(Boolean);
  const setSelected = (next) => setData({ ...data, amenities: next });
  const [customInput, setCustomInput] = useState("");

  const toggle = (name) => {
    if (selected.includes(name)) setSelected(selected.filter((x) => x !== name));
    else setSelected([...selected, name]);
  };
  const addCustom = () => {
    const t = customInput.trim();
    if (!t) return;
    if (!selected.includes(t)) setSelected([...selected, t]);
    setCustomInput("");
  };

  return (
    <div>
      <div className="mb-5">
        <h4 className="text-sm font-semibold text-slate-900 mb-1">Selected amenities</h4>
        <p className="text-xs text-slate-500 mb-3">
          {selected.length === 0
            ? "Nothing selected yet — tap chips below to add."
            : `${selected.length} amenit${selected.length === 1 ? "y" : "ies"} selected.`}
        </p>
        {selected.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {selected.map((name) => (
              <span
                key={name}
                className="inline-flex items-center gap-1 text-xs font-medium bg-[#162a52] text-white rounded-md pl-2 pr-1 py-1"
              >
                {name}
                <button onClick={() => toggle(name)} className="text-white/70 hover:text-white" title="Remove">
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="mb-5">
        <h4 className="text-sm font-semibold text-slate-900 mb-1">Add common amenities</h4>
        <p className="text-xs text-slate-500 mb-3">Tap any chip to add or remove it from the selection.</p>
        <div className="flex flex-wrap gap-2">
          {AMENITY_PRESETS.map((preset) => {
            const name = asName(preset);
            const on = selected.includes(name);
            return (
              <button
                key={name}
                onClick={() => toggle(name)}
                className={`text-xs font-medium rounded-md px-2.5 py-1.5 ring-1 ring-inset transition-colors ${
                  on ? "bg-[#c79a3a]/10 text-[#7a5c1a] ring-[#c79a3a]/40" : "bg-white text-slate-600 ring-slate-200 hover:bg-slate-50"
                }`}
              >
                {name}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <h4 className="text-sm font-semibold text-slate-900 mb-1">Custom amenity</h4>
        <p className="text-xs text-slate-500 mb-3">Type anything not in the list and press Enter or click Add.</p>
        <div className="flex items-center gap-2">
          <input
            value={customInput}
            onChange={(e) => setCustomInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCustom(); } }}
            placeholder="e.g. Pet-friendly park"
            className="flex-1 text-sm rounded-lg ring-1 ring-inset ring-slate-300 px-3 py-2 focus:outline-none focus:ring-blue-500"
          />
          <button
            onClick={addCustom}
            disabled={!customInput.trim()}
            className="text-sm font-medium px-3 py-2 rounded-lg bg-[#162a52] text-white hover:bg-[#0f1e3d] disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-1"
          >
            <Plus className="w-4 h-4" />Add
          </button>
        </div>
      </div>
    </div>
  );
}
